/**
 * F37 (Wave 2D) — resolve a `badge_catalog.icon_code` to a design-token icon
 * component (D13 — no S3 upload at MVP, `icon_code` references a
 * `@bravemove/design-tokens/icons` component).
 *
 * The code is matched against the UI registry, then the Status registry, then a
 * small alias table for the Wave-1-seeded codes (`trending-up`, `handshake`,
 * `users`, `zap`) which do NOT exist verbatim in any registry. Unresolved codes
 * render a neutral fallback glyph so the CMS never crashes on legacy / mis-typed
 * codes.
 *
 * The picker choices + lookup tables live in the sibling `badgeIcons.ts` module
 * (keeps this file fast-refresh-clean).
 */
import {
  StatusIcon,
  UIIcon,
  type StatusIconName,
  type UIIconName,
} from '@bravemove/design-tokens/icons';
import { SEEDED_ICON_ALIAS, STATUS_ICON_NAME_SET, UI_ICON_NAME_SET } from './badgeIcons';

export interface BadgeIconProps {
  readonly code: string;
  readonly size?: number;
  readonly className?: string;
}

/**
 * Render the icon for a badge `icon_code`. Falls back to a neutral `star` glyph
 * when the code resolves to nothing (so the table never shows a blank cell).
 */
export function BadgeIcon({ code, size = 18, className }: BadgeIconProps) {
  return (
    <span className={className} data-testid={`badge-icon-${code}`} aria-hidden="true">
      {renderIcon(code, size)}
    </span>
  );
}

function renderIcon(code: string, size: number) {
  if (UI_ICON_NAME_SET.has(code)) {
    return <UIIcon name={code as UIIconName} size={size} color="currentColor" />;
  }
  if (STATUS_ICON_NAME_SET.has(code)) {
    return <StatusIcon name={code as StatusIconName} size={size} color="currentColor" />;
  }
  const alias = SEEDED_ICON_ALIAS[code];
  if (alias) {
    return alias.kind === 'ui' ? (
      <UIIcon name={alias.name} size={size} color="currentColor" />
    ) : (
      <StatusIcon name={alias.name} size={size} color="currentColor" />
    );
  }
  // Unknown / mis-typed code → neutral fallback glyph.
  return <UIIcon name="star" size={size} color="currentColor" />;
}
